//client/src/components/moonbeams/MoonbeamPreview.js
//
// Public Moonbeam viewer. Anyone with the card URL can land here — no auth.
//
// URL TOKENS recognized:
//   ?r=xxxxxxxxxxxx     per-email-recipient view token
//   ?l=xxxxxxxxxxxxxxxx shareable-link token
//
// NAVBAR REVISION:
//   - The stripped, card-specific top bar that used to live in this file has
//     been replaced with the standard site <Navbar />, so a recipient sees the
//     same navigation as the rest of the site (and their own name if they
//     happen to be logged in, or "Sign In" if not).
//   - The card-specific controls that the old bar hosted — the
//     "Moonbeam / About the Sender" view switcher and the "Create Your Own"
//     CTA dropdown — were NOT removed; they moved into a sub-bar that sits
//     directly beneath the navbar. All card content (the card itself, the
//     business/sender profile, and the CTA) is unchanged.
//   - The loading, not-found ("error"), and "no longer available" ("gone")
//     states now also render inside the standard navbar layout so those pages
//     have the site nav at the top too.
//   - Because the standard navbar replaces the old fixed 64px bar, the layout
//     is no longer a locked full-viewport flex column. The card area is given
//     a comfortable height via min-height math that accounts for the navbar
//     and the sub-bar instead of pinning to the viewport.

import { useState, useEffect, useRef } from 'react';
import moonbeamService from '../../services/moonbeamService.js';
import businessProfileService from '../../services/businessProfileService.js';
import { useSettings } from '../../hooks/useSettings.js';
import Navbar from '../layout/Navbar.js';
import SenderProfile from './SenderProfile.js';
import CardReactions from './CardReactions.js';
import api from '../../services/api.js';

export default function MoonbeamPreview({ uuid, recipientToken, linkToken }) {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // gone: true when the server returns 410 (deactivated shareable link or
  // expired card). Rendered as its own friendly state rather than a hard
  // error — the recipient doesn't need to feel like something broke.
  const [gone, setGone] = useState(false);
  const [activeView, setActiveView] = useState('card'); // 'card' | 'profile'
  const [senderProfile, setSenderProfile] = useState(null);
  const [senderLinks, setSenderLinks] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  const [ctaLinks, setCtaLinks] = useState([{ name: 'Create Your Own Moonbeam', url: '/' }]);
  const [ctaDropdownOpen, setCtaDropdownOpen] = useState(false);
  const ctaContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const hasFetched = useRef(false);
  const { settings } = useSettings();

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEWPORT HEIGHT MESSAGING - Send available height to iframe for scaling
  // ═══════════════════════════════════════════════════════════════════════════
  // The card lives below the site navbar (~56–64px) and a sub-bar that holds
  // the view tabs + CTA (~64px, taller on mobile where the CTA wraps to its
  // own row). We subtract a generous chunk so the card iframe fits without
  // forcing a page scroll, mirroring the old fixed-layout behavior.
  const sendViewportHeight = () => {
    if (iframeRef.current?.contentWindow) {
      const navbarHeight = window.innerWidth < 640 ? 56 : 64;
      const subBarHeight = window.innerWidth < 640 ? 116 : 64;
      const padding = 48;
      const availableHeight = window.innerHeight - navbarHeight - subBarHeight - padding;

      iframeRef.current.contentWindow.postMessage({
        type: 'setViewportHeight',
        height: Math.max(360, availableHeight),
      }, '*');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      sendViewportHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleIframeLoad = () => {
    sendViewportHeight();
  };

  // Close dropdown when clicking/tapping outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ctaContainerRef.current && !ctaContainerRef.current.contains(event.target)) {
        setCtaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Fetch card data and CTA links
  useEffect(() => {
    if (!uuid || hasFetched.current) return;
    hasFetched.current = true;

    moonbeamService.viewCard(uuid, { recipientToken, linkToken })
      .then((data) => {
        setCardData(data);
        if (data.userid) {
          fetchSenderProfile(data.userid);
        }
      })
      .catch((err) => {
        // 410 Gone = expired card or deactivated shareable link. Both get
        // the same gentle "no longer available" treatment.
        if (err.status === 410) {
          setGone(true);
        } else {
          setError(err.data?.error || 'Card not found');
        }
      })
      .finally(() => setLoading(false));

    // Fetch CTA links
    api.get('/links/cta-links', false)
      .then((data) => {
        if (data.links && data.links.length > 0) {
          setCtaLinks(data.links);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch CTA links:', err);
      });
  }, [uuid, recipientToken, linkToken]);

  // Fetch sender's public business profile
  const fetchSenderProfile = async (userid) => {
    setProfileLoading(true);
    try {
      const data = await businessProfileService.getPublicProfile(userid);
      setSenderProfile(data.profile);
      setSenderLinks(data.links || []);
    } catch (err) {
      setSenderProfile(null);
      setSenderLinks([]);
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle CTA link click — open window FIRST to preserve user gesture context
  const handleCtaLinkClick = (link) => {
    setCtaDropdownOpen(false);
    const ctaUrl = link.url || '/';
    window.open(ctaUrl, '_blank', 'noopener,noreferrer');

    try {
      businessProfileService.trackAdminLinkClick(
        link.shortcode || 'cta-link',
        cardData?.moonbeamid || null,
        null
      );
    } catch (err) {
      console.error('Failed to track CTA click:', err);
    }
  };

  // ─── Loading / error / gone states ──────────────────────────────────────
  // All three render INSIDE the standard navbar now, so the public card pages
  // (including "card not found" and "no longer available") carry the same site
  // navigation at the top as the rest of the app.
  if (loading) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-gold-dark dark:text-gold text-xl animate-pulse">Loading your Moonbeam...</div>
        </div>
      </div>
    );
  }

  // Gone state — shareable link deactivated, or card expired.
  if (gone) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <p className="text-4xl mb-4">🌙</p>
            <p className="text-royal-900 dark:text-white text-lg mb-2">This Moonbeam is no longer available</p>
            <p className="text-royal-500 dark:text-royal-200 text-base">
              The link may have been deactivated or the card has expired. Please reach out to whoever sent it to you if you'd like to see it again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center">
            <p className="text-4xl mb-4">🌙</p>
            <p className="text-royal-500 dark:text-royal-200 text-lg">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const showProfileTab = senderProfile && !profileLoading;

  // Reaction config extracted from the card view response. The server
  // already applied the global kill switch and per-template flag, so we
  // just trust reactionsAllowed here.
  const reactionsAllowed = !!cardData?.reactionsAllowed;
  const reactionEmojis = cardData?.reactionEmojis || [];
  const viewerToken = cardData?.viewerToken || null;
  const viewerTokenType = cardData?.viewerTokenType || null;

  // CTA Dropdown Component — unchanged behavior, now lives in the sub-bar.
  const CtaDropdown = ({ isMobile = false }) => (
    <div className={`relative ${isMobile ? 'w-full' : ''}`}>
      <button
        onClick={() => setCtaDropdownOpen(!ctaDropdownOpen)}
        className={`inline-flex items-center justify-center gap-1.5 btn-gold text-sm py-2 px-6 ${isMobile ? 'w-full' : ''}`}
      >
        Create Your Own
        <svg
          className={`w-4 h-4 transition-transform ${ctaDropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {ctaDropdownOpen && (
        <div className={`absolute ${isMobile ? 'left-0 right-0' : 'right-0'} mt-2 bg-white dark:bg-royal-800 border border-royal-200 dark:border-royal-600 rounded-lg shadow-xl z-50 overflow-hidden min-w-[220px]`}>
          {ctaLinks.map((link, index) => (
            <button
              key={link.linkid || index}
              onClick={() => handleCtaLinkClick(link)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-royal-800 dark:text-royal-50 hover:bg-cream dark:hover:bg-royal-700 hover:text-gold transition-colors border-b border-royal-200 dark:border-royal-700 last:border-b-0"
            >
              <span className="text-sm font-medium">{link.name}</span>
              <svg className="w-4 h-4 flex-shrink-0 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Shared tab-button styling for the sub-bar view switcher.
  const tabClass = (isActive) =>
    `px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
      isActive
        ? 'bg-gold/15 text-gold-dark dark:text-gold border-gold/30'
        : 'text-royal-700 dark:text-royal-50 hover:text-gold border-transparent dark:hover:text-gold'
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-cream dark:bg-bg-dark transition-colors duration-300" ref={ctaContainerRef}>
      {/* Standard site navbar */}
      <Navbar />

      {/* ─── Card sub-bar ──────────────────────────────────────────────────
          Holds the Moonbeam / About-the-Sender view switcher and the
          "Create Your Own" CTA. Sits directly beneath the navbar. On mobile
          the CTA drops to its own full-width row below the tabs. */}
      <div className="flex-shrink-0 bg-cream/80 dark:bg-royal-900/60 border-b border-royal-200/60 dark:border-royal-700">
        <div className="max-w-5xl mx-auto px-4">
          {/* Desktop / tablet: tabs left, CTA right */}
          <div className="hidden sm:flex items-center justify-between h-16 gap-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setActiveView('card')} className={tabClass(activeView === 'card')}>
                Moonbeam
              </button>
              {showProfileTab && (
                <button onClick={() => setActiveView('profile')} className={tabClass(activeView === 'profile')}>
                  About the Sender
                </button>
              )}
            </div>
            <CtaDropdown />
          </div>

          {/* Mobile: tabs row, then full-width CTA row */}
          <div className="sm:hidden py-3 space-y-3">
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => setActiveView('card')} className={tabClass(activeView === 'card')}>
                Moonbeam
              </button>
              {showProfileTab && (
                <button onClick={() => setActiveView('profile')} className={tabClass(activeView === 'profile')}>
                  About the Sender
                </button>
              )}
            </div>
            <CtaDropdown isMobile={true} />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 py-6">
        {activeView === 'card' ? (
          <div className="max-w-4xl mx-auto px-4 relative" style={{ minHeight: '60vh' }}>
            <iframe
              ref={iframeRef}
              srcDoc={cardData.html}
              title="Moonbeam"
              className="w-full border-0 rounded-2xl overflow-hidden shadow-2xl"
              style={{ height: 'calc(100vh - 240px)', minHeight: '360px' }}
              onLoad={handleIframeLoad}
            />

            {/* ─── Floating Reaction Bar ───────────────────────────────── */}
            {reactionsAllowed && reactionEmojis.length > 0 && (
              <div className="pointer-events-none absolute inset-x-0 bottom-4 sm:bottom-6 flex justify-center px-4">
                <div className="pointer-events-auto">
                  <CardReactions
                    moonbeamUuid={uuid}
                    emojis={reactionEmojis}
                    viewerToken={viewerToken}
                    viewerTokenType={viewerTokenType}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-4">
            <SenderProfile
              profile={senderProfile}
              links={senderLinks}
              moonbeamid={cardData?.moonbeamid}
              recipientid={null}
            />
          </div>
        )}
      </div>
    </div>
  );
}